const W = 1080, H = 1080;
const INK = "#111827", GOLD = "#d4a017", PAPER = "#fdf6e3";
const FONT = "Inter, 'Helvetica Neue', Arial, sans-serif";

export const CARD_THEMES = {
  ink: { id: "ink", label: "Ink", bg: INK, grain: "253,246,227", frame: GOLD, text: "#ffffff", sub: "#e5e7eb", muted: "#9ca3af", badgeBg: GOLD, badgeText: INK },
  paper: { id: "paper", label: "Cream paper", bg: PAPER, grain: "17,24,39", frame: INK, text: INK, sub: "#374151", muted: "#6b7280", badgeBg: INK, badgeText: GOLD },
  newsprint: { id: "newsprint", label: "Yellowed newsprint", bg: "#efe1bd", grain: "90,60,20", frame: INK, text: INK, sub: "#3b3222", muted: "#7a6a4a", badgeBg: INK, badgeText: GOLD },
};


const loadImage = (src) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

const wrap = (ctx, text, maxWidth) => {
  const words = text.split(/\s+/), lines = [];
  let line = "";
  for (const w of words) {
    const t = line ? `${line} ${w}` : w;
    if (ctx.measureText(t).width > maxWidth && line) { lines.push(line); line = w; } else line = t;
  }
  if (line) lines.push(line);
  return lines;
};

const fitLines = (ctx, text, maxWidth, maxLines, start, weight, min = 40) => {
  for (let s = start; s >= min; s -= 2) {
    ctx.font = `${weight} ${s}px ${FONT}`;
    const lines = wrap(ctx, text, maxWidth);
    if (lines.length <= maxLines) return { lines, size: s };
  }
  ctx.font = `${weight} ${min}px ${FONT}`;
  return { lines: wrap(ctx, text, maxWidth).slice(0, maxLines), size: min };
};

export async function drawShareCard(canvas, { badge, greeting, title: name, body: details, dateLabel, footer = "What happened on this day? → oddlytrue.co", theme = "ink" }) {
  const T = CARD_THEMES[theme] || CARD_THEMES.ink;
  if (document.fonts?.ready) await document.fonts.ready;
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = T.bg; ctx.fillRect(0, 0, W, H);
  for (let i = 0; i < 1400; i++) {
    ctx.fillStyle = `rgba(${T.grain},${Math.random() * 0.05})`;
    ctx.fillRect(Math.random() * W, Math.random() * H, 2, 2);
  }
  ctx.strokeStyle = T.frame; ctx.lineWidth = 6; ctx.strokeRect(48, 48, W - 96, H - 96);
  ctx.lineWidth = 2; ctx.strokeRect(72, 72, W - 144, H - 144);

  const seal = await loadImage("/seal-oddlytrue-red-wax.png");
  if (seal) {
    ctx.drawImage(seal, W / 2 - 80, 140, 160, 160);
  }

  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  const badgeText = (badge || "OddlyTrue").toUpperCase();
  ctx.font = `700 26px ${FONT}`; ctx.letterSpacing = "6px";
  const bw = ctx.measureText(badgeText).width + 70;
  ctx.fillStyle = T.badgeBg; ctx.beginPath(); ctx.roundRect(W / 2 - bw / 2, 352, bw, 56, 28); ctx.fill();
  ctx.fillStyle = T.badgeText; ctx.fillText(badgeText, W / 2 + 3, 381); ctx.letterSpacing = "0px";

  ctx.fillStyle = GOLD; ctx.font = `600 44px ${FONT}`;
  ctx.fillText(greeting || "On this day", W / 2, 452);

  ctx.fillStyle = T.text;
  const title = fitLines(ctx, name, W - 220, 3, 96, 800);
  const lh = title.size * 1.08;
  const titleTop = 536;
  title.lines.forEach((l, i) => ctx.fillText(l, W / 2, titleTop + i * lh));
  let y = titleTop + title.lines.length * lh + 10;

  ctx.strokeStyle = GOLD; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(W / 2 - 120, y); ctx.lineTo(W / 2 + 120, y); ctx.stroke();
  y += 50;

  ctx.fillStyle = T.sub;
  const available = 880 - y;
  let body = null;
  for (let s = 34; s >= 22; s -= 2) {
    ctx.font = `500 ${s}px ${FONT}`;
    const lines = wrap(ctx, details || "", W - 240);
    if (lines.length * s * 1.4 <= available) { body = { lines, size: s }; break; }
  }
  if (!body) { ctx.font = `500 22px ${FONT}`; body = { lines: wrap(ctx, details || "", W - 240).slice(0, Math.max(1, Math.floor(available / 31))), size: 22 }; }
  body.lines.forEach((l, i) => ctx.fillText(l, W / 2, y + i * body.size * 1.4));

  ctx.fillStyle = T.muted; ctx.font = `600 28px ${FONT}`; ctx.letterSpacing = "6px";
  ctx.fillText((dateLabel || "").toUpperCase(), W / 2, 912); ctx.letterSpacing = "0px";
  ctx.fillStyle = GOLD; ctx.font = `700 34px ${FONT}`;
  ctx.fillText(footer, W / 2, 968);
  return canvas;
}

export async function renderShareCard(data) {
  const canvas = await drawShareCard(document.createElement("canvas"), data);
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), "image/png"));
}

export const shareCardFilename = (data) => `oddlytrue-${(data.title || "day").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 60)}-${data.theme || "ink"}.png`;

export async function downloadShareCard(data) {
  const blob = await renderShareCard(data);
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: shareCardFilename(data) });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export async function shareCard(data, url) {
  const blob = await renderShareCard(data);
  const file = new File([blob], shareCardFilename(data), { type: "image/png" });
  const payload = { title: `${data.greeting || "On this day"} ${data.title}`, text: `${data.body || ""}\n${url}`, files: [file] };
  if (navigator.canShare?.({ files: [file] })) { await navigator.share(payload); return "shared"; }
  if (navigator.share) { await navigator.share({ title: payload.title, text: payload.text, url }); return "shared"; }
  return "unsupported";
}
